import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExclamationTriangle, FaTrash, FaCheckCircle } from 'react-icons/fa';

/**
 * Modern Confirmation Dialog Component
 * Replaces window.confirm() with a beautiful, customizable modal
 */
export default function ConfirmDialog({
    isOpen,
    onConfirm,
    onCancel,
    title = 'Confirm Action',
    message = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    danger = false,
    icon = 'warning' // 'warning', 'trash', 'check'
}) {

    // Handle keyboard events
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            if (e.key === 'Escape') onCancel();
            if (e.key === 'Enter') onConfirm();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onConfirm, onCancel]);

    const getIcon = () => {
        const iconClass = danger ? 'text-red-600' : 'text-yellow-600';
        const size = 32;

        switch (icon) {
            case 'trash':
                return <FaTrash size={size} className={iconClass} />;
            case 'check':
                return <FaCheckCircle size={size} className="text-blue-600" />;
            default:
                return <FaExclamationTriangle size={size} className={iconClass} />;
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={onCancel}
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header with Icon */}
                        <div className="p-8 pb-6 text-center">
                            <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${danger
                                    ? 'bg-red-50'
                                    : 'bg-yellow-50'
                                }`}>
                                {getIcon()}
                            </div>

                            <h3 className="text-2xl font-bold text-gray-900 mb-3">
                                {title}
                            </h3>

                            <p className="text-gray-600 leading-relaxed font-medium">
                                {message}
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="px-8 pb-8 pt-4 flex gap-3">
                            <button
                                onClick={onCancel}
                                className="flex-1 px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all active:scale-95"
                            >
                                {cancelText}
                            </button>
                            <button
                                onClick={onConfirm}
                                className={`flex-1 px-6 py-3.5 rounded-xl font-bold transition-all active:scale-95 shadow-lg ${danger
                                        ? 'bg-red-600 hover:bg-red-700 text-white shadow-red-200'
                                        : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200'
                                    }`}
                            >
                                {confirmText}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
